import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Capacitor } from '@capacitor/core'

// Check if running on native mobile platform, PWA standalone mode on mobile, or mobile device
// User can force desktop mode by adding ?desktop=1 or toggling in mobile menu
const isNativeMobile = () => {
  // Allow user to force desktop mode
  if (localStorage.getItem('forceDesktop') === 'true') return false

  const isNative = Capacitor.isNativePlatform()
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true // iOS Safari

  // Check if this is a mobile-sized device with touch support
  const isMobileDevice = window.innerWidth <= 768 &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0)

  // PWA on desktop should use desktop routes — only PWA + mobile screen = mobile
  const isMobilePWA = isPWA && isMobileDevice

  const result = isNative || isMobilePWA || isMobileDevice

  return result
}

const routes = [
  {
    path: '/',
    redirect: () => {
      if (isNativeMobile()) return '/m/home'
      // User ที่มี company restriction → ไปหน้าจัดการพนักงาน
      const authStore = useAuthStore()
      if (authStore.companyAccess && authStore.companyAccess.length > 0) return '/employees'
      return '/dashboard'
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresGuest: true },
    beforeEnter: (to, from, next) => {
      // Redirect to mobile login if on native platform
      if (isNativeMobile()) {
        next('/m/login')
      } else {
        next()
      }
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/employees',
    name: 'Employees',
    component: () => import('@/views/employees/EmployeesView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/interns',
    name: 'Interns',
    component: () => import('@/views/interns/InternsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin', 'manager'] }
  },
  {
    path: '/probationary',
    name: 'Probationary',
    component: () => import('@/views/probationary/ProbationaryView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin', 'manager'] }
  },
  {
    path: '/attendance',
    name: 'Attendance',
    component: () => import('@/views/attendance/AttendanceView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/qr-management',
    name: 'QRManagement',
    component: () => import('@/views/qr/QRManagementView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/qr-scan',
    name: 'QRScan',
    component: () => import('@/views/qr/QRScanView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/rfid-scan',
    name: 'RFIDScan',
    component: () => import('@/views/rfid/RFIDScanView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/attendance-report',
    name: 'AttendanceReport',
    component: () => import('@/views/qr/AttendanceReportView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/rfid-management',
    name: 'RFIDManagement',
    component: () => import('@/views/rfid/RFIDManagementView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/positions',
    name: 'Positions',
    component: () => import('@/views/settings/PositionsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/settings/deduction-rules',
    name: 'DeductionRulesSettings',
    component: () => import('@/views/settings/DeductionRulesSettings.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/settings/shifts',
    name: 'ShiftSettings',
    component: () => import('@/views/settings/ShiftSettingsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/payroll',
    name: 'Payroll',
    component: () => import('@/views/PayrollView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/views/reports/ReportsView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/ytd-summary',
    name: 'YTDSummary',
    component: () => import('@/views/payroll/YTDSummaryView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/admin/AdminView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'super_admin'] }
  },
  {
    path: '/news',
    name: 'News',
    component: () => import('@/views/NewsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/password-management',
    name: 'PasswordManagement',
    component: () => import('@/views/PasswordManagementView.vue'),
    meta: { requiresAuth: true, roles: ['hr', 'super_admin'] }
  },
  {
    path: '/leave',
    name: 'LeaveManagement',
    component: () => import('@/views/LeaveManagementFunctional.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/training',
    name: 'TrainingManagement',
    component: () => import('@/views/TrainingManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/manager',
    name: 'ManagerInbox',
    component: () => import('@/views/ManagerInbox.vue'),
    meta: { requiresAuth: true, roles: ['manager', 'hr', 'admin', 'super_admin'] }
  },
  {
    path: '/notifications',
    name: 'NotificationCenter',
    component: () => import('@/views/NotificationCenterView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/calendar',
    name: 'LeaveCalendar',
    component: () => import('@/views/calendar/CalendarView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/signatures',
    name: 'SignatureManagement',
    component: () => import('@/views/SignatureManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization',
    name: 'OrganizationChart',
    component: () => import('@/views/organization/OrganizationChartView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/flowchart',
    name: 'OrganizationFlowChart',
    component: () => import('@/views/organization/OrganizationFlowChart.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/assets',
    name: 'AssetManagement',
    component: () => import('@/views/assets/AssetManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/organization/chart-types',
    name: 'ChartTypes',
    component: () => import('@/views/organization/ChartTypesView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'super_admin', 'hr'] }
  },
  {
    path: '/holidays',
    name: 'HolidaysManagement',
    component: () => import('@/views/HolidaysManagement.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/attendance-corrections',
    name: 'AttendanceCorrections',
    component: () => import('@/views/AttendanceCorrectionView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/overtime',
    name: 'OvertimeManagement',
    component: () => import('@/views/OvertimeManagementView.vue'),
    meta: { requiresAuth: true, roles: ['admin', 'hr', 'super_admin'] }
  },
  {
    path: '/leave/balance',
    name: 'LeaveBalance',
    component: () => import('@/views/LeaveBalance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/leave/balance-management',
    name: 'LeaveBalanceManagement',
    component: () => import('@/views/LeaveBalanceManagement.vue'),
    meta: { requiresAuth: true, requiresRole: ['hr', 'admin', 'super_admin'] }
  },
  {
    path: '/leave/history',
    name: 'LeaveHistory',
    component: () => import('@/views/LeaveHistory.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/employee/payslips',
    name: 'EmployeePayslips',
    component: () => import('@/views/EmployeePayslips.vue'),
    meta: { requiresAuth: true }
  },

  // =====================================
  // Intern System Routes (Separate Auth)
  // =====================================
  {
    path: '/intern/login',
    name: 'InternLogin',
    component: () => import('@/views/intern/InternLoginView.vue'),
    meta: {
      requiresGuest: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/dashboard',
    name: 'InternDashboard',
    component: () => import('@/views/intern/InternDashboardView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/work-logs',
    name: 'InternWorkLogs',
    component: () => import('@/views/intern/InternWorkLogsView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/work-logs/new',
    name: 'InternWorkLogNew',
    component: () => import('@/views/intern/InternWorkLogFormView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/work-logs/:id/edit',
    name: 'InternWorkLogEdit',
    component: () => import('@/views/intern/InternWorkLogFormView.vue'),
    props: true,
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/work-logs/:id',
    name: 'InternWorkLogDetail',
    component: () => import('@/views/intern/InternWorkLogDetailView.vue'),
    props: true,
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/projects',
    name: 'InternProjects',
    component: () => import('@/views/intern/InternProjectsView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/projects/:id',
    name: 'InternProjectDetail',
    component: () => import('@/views/intern/InternProjectDetailView.vue'),
    props: true,
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/evaluations',
    name: 'InternEvaluations',
    component: () => import('@/views/intern/InternEvaluationsView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },
  {
    path: '/intern/qr-scan',
    name: 'InternQRScan',
    component: () => import('@/views/intern/InternQRScanView.vue'),
    meta: {
      requiresInternAuth: true,
      layout: 'none',
      isInternRoute: true
    }
  },

  // =====================================
  // Mobile App Routes
  // =====================================
  {
    path: '/m/login',
    name: 'MobileLogin',
    component: () => import('@/views/mobile/MobileLogin.vue'),
    meta: {
      requiresGuest: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/home',
    name: 'MobileHome',
    component: () => import('@/views/mobile/MobileHome.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/attendance',
    name: 'MobileAttendance',
    component: () => import('@/views/mobile/MobileAttendance.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/leave',
    name: 'MobileLeave',
    component: () => import('@/views/mobile/MobileLeave.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/leave/request',
    name: 'MobileLeaveRequest',
    component: () => import('@/views/mobile/MobileLeaveRequest.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/leave/approval',
    name: 'MobileLeaveApproval',
    component: () => import('@/views/mobile/MobileLeaveApproval.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true,
      roles: ['manager', 'hr', 'admin', 'super_admin']
    }
  },
  {
    path: '/m/payslip',
    name: 'MobilePayslip',
    component: () => import('@/views/mobile/MobilePayslip.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/menu',
    name: 'MobileMenu',
    component: () => import('@/views/mobile/MobileMenu.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/passkeys',
    name: 'MobilePasskeys',
    component: () => import('@/views/mobile/MobilePasskeys.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/correction/request',
    name: 'MobileCorrectionRequest',
    component: () => import('@/views/mobile/MobileCorrectionRequest.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m/correction/manage',
    name: 'MobileCorrectionManage',
    component: () => import('@/views/mobile/MobileCorrectionManage.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true,
      roles: ['hr', 'admin', 'super_admin']
    }
  },
  {
    path: '/m/overtime-manage',
    name: 'MobileOTManage',
    component: () => import('@/views/mobile/MobileOTManage.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true,
      roles: ['hr', 'admin', 'super_admin']
    }
  },
  {
    path: '/m/notifications',
    name: 'MobileNotifications',
    component: () => import('@/views/mobile/MobileNotifications.vue'),
    meta: {
      requiresAuth: true,
      layout: 'none',
      isMobileRoute: true
    }
  },
  {
    path: '/m',
    redirect: '/m/home'
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Handle ?desktop=1 query param — switch to desktop mode on mobile
  if (to.query.desktop === '1' || to.query.desktop === 'true') {
    localStorage.setItem('forceDesktop', 'true')
  } else if (to.query.desktop === '0' || to.query.mobile === '1') {
    localStorage.removeItem('forceDesktop')
  }

  // IMPORTANT: Redirect to mobile routes if on native platform
  // This should be the FIRST check before any other logic
  if (isNativeMobile() && !to.meta.isMobileRoute && !to.meta.isInternRoute) {
    console.log('📱 Native platform detected, redirecting to mobile route')

    // Map desktop routes to mobile routes
    const mobileRouteMap = {
      '/': '/m/home',
      '/login': '/m/login',
      '/dashboard': '/m/home',
      '/attendance': '/m/attendance',
      '/qr-scan': '/m/attendance',
      '/leave': '/m/leave',
      '/employee/payslips': '/m/payslip',
      '/attendance-corrections': '/m/correction/manage'
    }

    const mobilePath = mobileRouteMap[to.path] || '/m/home'

    // If user is not authenticated and trying to access protected route
    if (!authStore.isAuthenticated && to.meta.requiresAuth) {
      next('/m/login')
      return
    }

    // If user is authenticated and trying to access login
    if (authStore.isAuthenticated && to.path === '/login') {
      next('/m/home')
      return
    }

    // Redirect to appropriate mobile route
    next(mobilePath)
    return
  }

  // Handle intern routes separately
  if (to.meta.isInternRoute) {
    const internToken = localStorage.getItem('intern_access_token')
    const internUser = localStorage.getItem('intern_user')

    // Check intern authentication
    if (to.meta.requiresInternAuth) {
      if (!internToken || !internUser) {
        next('/intern/login')
        return
      }

      // Verify token is not expired (basic check)
      try {
        const payload = JSON.parse(atob(internToken.split('.')[1]))
        const currentTime = Date.now() / 1000

        if (payload.exp && payload.exp < currentTime) {
          // Token expired
          localStorage.removeItem('intern_access_token')
          localStorage.removeItem('intern_refresh_token')
          localStorage.removeItem('intern_user')
          next('/intern/login')
          return
        }
      } catch (error) {
        console.error('Error checking intern token:', error)
        next('/intern/login')
        return
      }
    }

    // Handle intern guest routes
    if (to.meta.requiresGuest && internToken && internUser) {
      next('/intern/dashboard')
      return
    }

    next()
    return
  }

  // Handle mobile routes separately
  if (to.meta.isMobileRoute) {
    // Mobile routes use the same authentication as the main HR system
    // If user has token but no user data, try to fetch profile
    if (authStore.token && !authStore.user && !authStore.isFetchingProfile) {
      try {
        await authStore.fetchProfile()
      } catch (error) {
        console.error('Failed to fetch profile for mobile route:', error)
        // If offline, try to restore from localStorage cache
        if (!navigator.onLine && !authStore.user) {
          authStore._restoreFromCache()
        }
        // Only redirect to login if we still don't have user data and auth is required
        if (!authStore.user && to.meta.requiresAuth) {
          next('/m/login')
          return
        }
      }
    }

    // Check if user is authenticated
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      // Last resort: if offline and has cached user, allow through
      if (!navigator.onLine && authStore.token) {
        authStore._restoreFromCache()
      }
      if (!authStore.isAuthenticated) {
        next('/m/login')
        return
      }
    }

    // If user is authenticated and trying to access mobile login, redirect to mobile home
    if (to.meta.requiresGuest && authStore.isAuthenticated) {
      next('/m/home')
      return
    }

    next()
    return
  }

  // Regular HR system authentication
  // If user has token but no user data, try to fetch profile (will use cache if available)
  if (authStore.token && !authStore.user && !authStore.isFetchingProfile) {
    try {
      // fetchProfile now has built-in caching and deduplication
      await authStore.fetchProfile()
    } catch (error) {
      // Don't logout on network errors during navigation
      console.error('Failed to fetch profile during navigation:', error)
      // Only proceed if we still don't have user data
      if (!authStore.user && to.meta.requiresAuth) {
        next('/login')
        return
      }
    }
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next(isNativeMobile() ? '/m/login' : '/login')
  } else if (to.meta.requiresGuest && authStore.isAuthenticated && !to.meta.isInternRoute) {
    next(isNativeMobile() ? '/m/home' : '/')
  } else if (to.meta.roles && !authStore.hasRole(to.meta.roles)) {
    next(isNativeMobile() ? '/m/home' : '/')
  } else if (authStore.companyAccess && authStore.companyAccess.length > 0) {
    // User ที่มี company restriction → อนุญาตเฉพาะหน้าที่กำหนด
    const allowedPaths = [
      '/employees', '/payroll', '/ytd-summary', '/attendance-report',
      '/qr-scan', '/rfid-scan', '/leave', '/calendar', '/signatures', '/employee/payslips', '/assets', '/news',
      '/login', '/'
    ]
    const isAllowed = allowedPaths.some(p => to.path === p || to.path.startsWith(p + '/'))
    if (!isAllowed) {
      next('/employees')
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router